// Named imports + explicit constructor (avoids class-field-initializer +
// default-React-import interop that some minifier/transformer combos handle
// inconsistently, producing the dreaded "n is not a constructor" in prod).
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { logger } from "@/utils/logger";

/**
 * Error boundary specifically designed for code-split route chunks.
 *
 * Failure mode it catches:
 *   When we ship a new build, `vite build` produces new content-hashed chunk
 *   filenames (e.g. SEOManagement-B2eF4e1A.js). The deploy pipeline mirrors
 *   `dist/` with `--delete`, so old chunks are removed from the server. A
 *   user with a cached older `index.html` open in their browser still tries
 *   to lazy-import the OLD chunk name → 404 → `import()` throws TypeError /
 *   chunk load error → React's `<Suspense>` doesn't catch it (Suspense only
 *   handles loading, not errors) → without a boundary, the entire route
 *   subtree silently blanks.
 *
 * What this does:
 *   1. Catches the error and renders an actionable message instead of blank
 *   2. Detects "chunk load" / "failed to fetch dynamically imported" errors
 *      specifically — those mean a new version is live and a reload fixes it
 *   3. Offers a "Reload page" button that does `location.reload(true)` so
 *      the browser refetches the (no-cache) `index.html` and discovers the
 *      current chunk filenames
 *
 * Why "true" on reload: forces a cache bypass even when the cached index.html
 * has `cache-control: no-store` (some browsers still honor in-memory cache
 * for the current tab until a hard reload).
 */
interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}
export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error("Route render failed:", error, info);
  }

  private isChunkError(): boolean {
    const msg = (this.state.error?.message ?? "").toLowerCase();
    return (
      msg.includes("failed to fetch dynamically imported module") ||
      msg.includes("loading chunk") ||
      msg.includes("loading css chunk") ||
      msg.includes("importing a module script failed") ||
      msg.includes("module specifier")
    );
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const chunkErr = this.isChunkError();
    return (
      <div className="flex min-h-[50vh] items-center justify-center p-6">
        <div className="max-w-md w-full rounded-lg border bg-card p-6 text-center space-y-3 shadow-sm">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500" />
          <h2 className="text-xl font-bold">
            {chunkErr ? "A new version of the app is live" : "Something went wrong"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {chunkErr ? (
              <>Your tab has been open since a prior deploy and is trying to load an old asset that no longer exists. Reload to pick up the latest build.</>
            ) : (
              <>The page failed to render. Reload to retry — if it keeps happening, copy the error below and share it.</>
            )}
          </p>
          <Button onClick={() => window.location.reload()} className="w-full">
            <RefreshCw className="w-4 h-4 mr-2" /> Reload page
          </Button>
          {!chunkErr && (
            <details className="text-left" open>
              <summary className="text-xs cursor-pointer text-muted-foreground select-none">
                Error detail (copy this to share)
              </summary>
              <pre className="text-[10px] mt-2 bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap max-h-64">
                <strong>{this.state.error.name}: {this.state.error.message}</strong>
                {"\n\n"}
                {this.state.error.stack ?? "(no stack)"}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
